/**
 * SettingsListPanel — reusable name-first CRUD management panel matching the
 * Dashboard handoff (Departments, Job Titles, Employee Types, Work Locations,
 * Employment Statuses). Wired to the real backend; pass a `config`.
 *
 * config = { title, subtitle, icon (lucide component), endpoint, itemLabel,
 *            columnLabel?, pageSize?, extraField? }
 *
 * `extraField` is optional and adds ONE more editable column beside the name —
 * used by Work Locations for its timezone. Panels that omit it are unchanged.
 *   extraField = { key, label, placeholder?, hint?, width?, type? }
 * type: "countryTimezone" swaps the text input for a Country select plus a
 * Timezone select limited to that country's zones. Only the zone is saved.
 *
 * `checkboxField` is optional and adds ONE on/off flag, shown as a checkbox in
 * the modal and a Yes/— column in the table — used by Departments for
 * "Allow shift swaps". `tooltip` sits on an info icon beside the label.
 *   checkboxField = { key, label, tooltip?, width? }
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Info, Plus, Search, SquarePen, Trash2 } from "lucide-react";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import {
  COUNTRIES,
  COUNTRY_TIMEZONES,
  countryForTimezone,
  timezoneLabel,
} from "../../data/countryTimezones";

import { toast } from "../../utils/toast";
const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fieldStyle = (focused, invalid) => ({
  width: "100%",
  padding: "11px 14px",
  border: `1.5px solid ${invalid ? T.danger : focused ? T.orange : T.border}`,
  borderRadius: 8,
  font: `400 14px ${T.font}`,
  color: T.fg1,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 150ms",
});

const Required = () => <span style={{ color: T.danger }}> *</span>;

const FieldError = ({ children }) =>
  children ? (
    <div style={{ marginTop: 6, font: `400 11px/1.4 ${T.font}`, color: T.danger }}>
      {children}
    </div>
  ) : null;

const ItemModal = ({
  existing,
  itemLabel,
  placeholder,
  extraField,
  checkboxField,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(existing?.name || "");
  const [extra, setExtra] = useState(
    extraField ? existing?.[extraField.key] || "" : "",
  );
  // MySQL hands TINYINT(1) back as 0/1, so coerce rather than trust truthiness
  // of whatever shape arrives.
  const [checked, setChecked] = useState(
    checkboxField ? Number(existing?.[checkboxField.key]) === 1 : false,
  );
  const isCountryTz = extraField?.type === "countryTimezone";
  const [country, setCountry] = useState(
    isCountryTz ? countryForTimezone(existing?.[extraField.key]) : "",
  );
  const [countryFocused, setCountryFocused] = useState(false);
  const [focused, setFocused] = useState(false);
  const [extraFocused, setExtraFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const ref = useRef(null);
  useEffect(() => {
    setTimeout(() => ref.current?.focus(), 50);
  }, []);

  // Blocking Save silently — which is what this did before — leaves no clue
  // why nothing happened, so each missing field now says so under itself.
  const save = () => {
    const nextName = name.trim();
    const nextExtra = extra.trim();
    const next = {};
    if (!nextName) next.name = `${itemLabel} name is required`;
    if (isCountryTz && extraField.required && !country && !nextExtra)
      next.country = "Country is required";
    else if (extraField?.required && !nextExtra)
      next.extra = `${extraField.label} is required`;
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload = { name: nextName };
    if (extraField) payload[extraField.key] = nextExtra;
    if (checkboxField) payload[checkboxField.key] = checked;
    onSave(payload);
  };
  const clearError = (key) =>
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        // .main-header is a sticky element pinned at z-index: 1000 — at 400
        // this overlay (and the modal's own header/title inside it) rendered
        // BELOW it, invisibly painted over for the top ~100px band the nav
        // bar occupies.
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px,100%)",
          background: "var(--em-bg-card)",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 20px 40px -12px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            padding: "18px 22px",
            borderBottom: `1px solid ${T.divider}`,
          }}
        >
          <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
            {existing ? `Edit ${itemLabel}` : `Add ${itemLabel}`}
          </div>
        </div>
        <div style={{ padding: "20px 22px" }}>
          <label
            style={{
              display: "block",
              marginBottom: 6,
              font: `600 12px ${T.font}`,
              color: T.fg3,
            }}
          >
            {itemLabel} name
            <Required />
          </label>
          <input
            ref={ref}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearError("name");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") onClose();
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder || `Enter ${itemLabel.toLowerCase()}...`}
            style={fieldStyle(focused, !!errors.name)}
          />
          <FieldError>{errors.name}</FieldError>
          {isCountryTz && (
            <div style={{ marginTop: 16 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  font: `600 12px ${T.font}`,
                  color: T.fg3,
                }}
              >
                Country
                {extraField.required && <Required />}
              </label>
              <select
                value={country}
                onChange={(e) => {
                  const next = e.target.value;
                  const zones = COUNTRY_TIMEZONES[next] || [];
                  setCountry(next);
                  // Single-zone countries (most of them) need no second pick.
                  setExtra(zones.length === 1 ? zones[0] : "");
                  clearError("country");
                  clearError("extra");
                }}
                onFocus={() => setCountryFocused(true)}
                onBlur={() => setCountryFocused(false)}
                style={{
                  ...fieldStyle(countryFocused, !!errors.country),
                  background: "var(--em-bg-card)",
                  cursor: "pointer",
                }}
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <FieldError>{errors.country}</FieldError>
            </div>
          )}
          {isCountryTz && (
            <div style={{ marginTop: 16 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  font: `600 12px ${T.font}`,
                  color: T.fg3,
                }}
              >
                {extraField.label}
                {extraField.required && <Required />}
              </label>
              {(() => {
                const zones = COUNTRY_TIMEZONES[country] || [];
                // A saved zone outside the country list stays selectable so
                // editing an older location doesn't silently drop it.
                const options =
                  extra && !zones.includes(extra) ? [extra, ...zones] : zones;
                return (
                  <select
                    value={extra}
                    disabled={!options.length}
                    onChange={(e) => {
                      setExtra(e.target.value);
                      clearError("extra");
                    }}
                    onFocus={() => setExtraFocused(true)}
                    onBlur={() => setExtraFocused(false)}
                    style={{
                      ...fieldStyle(extraFocused, !!errors.extra),
                      background: "var(--em-bg-card)",
                      cursor: options.length ? "pointer" : "not-allowed",
                      opacity: options.length ? 1 : 0.6,
                    }}
                  >
                    <option value="">
                      {country ? "Select timezone" : "Select a country first"}
                    </option>
                    {options.map((tz) => (
                      <option key={tz} value={tz}>
                        {timezoneLabel(tz)} — {tz}
                      </option>
                    ))}
                  </select>
                );
              })()}
              <FieldError>{errors.extra}</FieldError>
              {extraField.hint && !errors.extra && (
                <div
                  style={{
                    marginTop: 6,
                    font: `400 11px/1.4 ${T.font}`,
                    color: T.fg7,
                  }}
                >
                  {extraField.hint}
                </div>
              )}
            </div>
          )}
          {extraField && !isCountryTz && (
            <div style={{ marginTop: 16 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  font: `600 12px ${T.font}`,
                  color: T.fg3,
                }}
              >
                {extraField.label}
                {extraField.required && <Required />}
              </label>
              <input
                value={extra}
                onChange={(e) => {
                  setExtra(e.target.value);
                  clearError("extra");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") onClose();
                }}
                onFocus={() => setExtraFocused(true)}
                onBlur={() => setExtraFocused(false)}
                placeholder={extraField.placeholder || ""}
                style={fieldStyle(extraFocused, !!errors.extra)}
              />
              <FieldError>{errors.extra}</FieldError>
              {extraField.hint && !errors.extra && (
                <div
                  style={{
                    marginTop: 6,
                    font: `400 11px/1.4 ${T.font}`,
                    color: T.fg7,
                  }}
                >
                  {extraField.hint}
                </div>
              )}
            </div>
          )}
          {checkboxField && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {/* Margins/padding reset explicitly: the global form styles give
                  labels and checkboxes their own spacing, which pushed the
                  info icon off the text's line. */}
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  margin: 0,
                  padding: 0,
                  cursor: "pointer",
                  font: `500 13px/16px ${T.font}`,
                  color: T.fg1,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  style={{
                    width: 16,
                    height: 16,
                    margin: 0,
                    padding: 0,
                    flexShrink: 0,
                    accentColor: T.orange,
                    cursor: "pointer",
                  }}
                />
                <span>{checkboxField.label}</span>
              </label>
              {checkboxField.tooltip && (
                <span
                  title={checkboxField.tooltip}
                  aria-label={checkboxField.tooltip}
                  role="img"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 16,
                    color: T.fg7,
                    cursor: "help",
                  }}
                >
                  <Info size={14} style={{ display: "block" }} />
                </span>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            padding: "12px 22px",
            borderTop: `1px solid ${T.divider}`,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={save}
            style={{
              padding: "9px 20px",
              borderRadius: 8,
              border: 0,
              cursor: "pointer",
              background: T.gradient,
              color: "#fff",
              font: `600 14px ${T.font}`,
              boxShadow: T.shadowBrand,
            }}
          >
            Save
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              cursor: "pointer",
              background: "var(--em-bg-card)",
              font: `600 14px ${T.font}`,
              color: T.fg1,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsListPanel = ({ config }) => {
  const {
    title,
    subtitle,
    icon: Icon,
    endpoint,
    itemLabel,
    columnLabel,
    placeholder,
    extraField,
    checkboxField,
    pageSize = 10,
  } = config;
  const baseURL = `${API_URL}${endpoint}`;

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // null | {mode:'add'} | {mode:'edit', item}
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await axios.get(baseURL, auth());
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(`Error fetching ${itemLabel}`, err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      if (modal?.mode === "edit" && modal.item) {
        await axios.put(`${baseURL}/${modal.item.id}`, payload, auth());
      } else {
        await axios.post(baseURL, payload, auth());
      }
      setModal(null);
      await fetchItems();
    } catch (err) {
      toast.error(
        err.response?.data?.error || `Error saving ${itemLabel.toLowerCase()}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${baseURL}/${deleteConfirm.id}`, auth());
      setDeleteConfirm(null);
      await fetchItems();
    } catch (err) {
      // 409 = still in use (e.g. a department employees are assigned to).
      // Nothing to retry in this dialog, so close it and just explain why.
      if (err.response?.status === 409) {
        setDeleteConfirm(null);
        toast.warning(err.response.data?.error, { duration: 7000 });
        return;
      }
      toast.error(
        err.response?.data?.error ||
          `Error deleting ${itemLabel.toLowerCase()}`,
      );
    }
  };

  const filtered = items.filter(
    (d) =>
      !search || (d.name || "").toLowerCase().includes(search.toLowerCase()),
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pg = Math.min(page, totalPages);
  const slice = filtered.slice((pg - 1) * pageSize, pg * pageSize);

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: T.orange50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {Icon && <Icon size={20} color={T.orange} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              font: `700 20px ${T.font}`,
              color: T.fg1,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
            {subtitle}
          </div>
        </div>
        <button
          onClick={() => setModal({ mode: "add" })}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "10px 18px",
            borderRadius: 10,
            border: 0,
            cursor: "pointer",
            background: T.gradient,
            color: "#fff",
            font: `700 13px ${T.font}`,
            boxShadow: T.shadowBrand,
          }}
        >
          <Plus size={15} /> Add {itemLabel}
        </button>
      </div>

      {/* Search + total */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ position: "relative", maxWidth: 280, flex: 1 }}>
          <Search
            size={13}
            color={T.fg7}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={`Search ${itemLabel.toLowerCase()}...`}
            style={{
              width: "100%",
              padding: "9px 12px 9px 30px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              font: `400 13px ${T.font}`,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        <span
          style={{
            font: `400 13px ${T.font}`,
            color: T.fg5,
            marginLeft: "auto",
          }}
        >
          Total: <strong style={{ color: T.fg1 }}>{items.length}</strong>
        </span>
      </div>

      {/* Table card */}
      <div
        style={{
          background: "var(--em-bg-card)",
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: T.shadowSm,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 20px",
            background: T.bgHead,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div style={{ flex: 1, font: `600 13px ${T.font}`, color: T.fg1 }}>
            {columnLabel || itemLabel}
          </div>
          {extraField && (
            <div
              style={{
                font: `600 13px ${T.font}`,
                color: T.fg1,
                width: extraField.width || 180,
                flexShrink: 0,
              }}
            >
              {extraField.label}
            </div>
          )}
          {checkboxField && (
            <div
              style={{
                font: `600 13px ${T.font}`,
                color: T.fg1,
                width: checkboxField.width || 140,
                flexShrink: 0,
              }}
            >
              {checkboxField.label}
            </div>
          )}
          <div
            style={{
              font: `600 13px ${T.font}`,
              color: T.fg1,
              minWidth: 80,
              textAlign: "right",
            }}
          >
            Actions
          </div>
        </div>
        {slice.length === 0 && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              font: `400 13px ${T.font}`,
              color: T.fg7,
            }}
          >
            No {itemLabel.toLowerCase()}s found.
          </div>
        )}
        {slice.map((item, i) => (
          <div
            key={item.id}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "14px 20px",
              borderBottom:
                i < slice.length - 1 ? `1px solid ${T.divider}` : "none",
              background: i % 2 === 0 ? T.bgCard : T.bgZebra,
            }}
          >
            <div style={{ flex: 1, font: `500 14px ${T.font}`, color: T.fg1 }}>
              {item.name}
            </div>
            {extraField && (
              <div
                style={{
                  width: extraField.width || 180,
                  flexShrink: 0,
                  font: `400 13px ${T.font}`,
                  color: item[extraField.key] ? T.fg3 : T.fg7,
                }}
              >
                {item[extraField.key] || "—"}
              </div>
            )}
            {checkboxField && (
              <div
                style={{
                  width: checkboxField.width || 140,
                  flexShrink: 0,
                  font: `400 13px ${T.font}`,
                  color: Number(item[checkboxField.key]) === 1 ? T.fg3 : T.fg7,
                }}
              >
                {Number(item[checkboxField.key]) === 1 ? "Yes" : "—"}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 6,
                minWidth: 80,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setModal({ mode: "edit", item })}
                title="Edit"
                style={{
                  padding: 0,
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  border: `1px solid ${T.border}`,
                  background: "transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: T.fg4,
                }}
              >
                <SquarePen size={13} />
              </button>
              <button
                onClick={() => setDeleteConfirm(item)}
                title="Delete"
                style={{
                  padding: 0,
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  border: "1px solid #fecaca",
                  background: T.danger50,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: T.danger,
                }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pg === 1}
            style={{
              padding: "7px 14px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: "var(--em-bg-card)",
              cursor: pg === 1 ? "default" : "pointer",
              font: `500 13px ${T.font}`,
              color: pg === 1 ? T.fg7 : T.fg1,
            }}
          >
            Prev
          </button>
          {[...Array(Math.min(totalPages, 5))].map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                border: `1px solid ${pg === i + 1 ? T.orange : T.border}`,
                background: pg === i + 1 ? T.bgBlack : "#fff",
                color: pg === i + 1 ? "#fff" : T.fg1,
                cursor: "pointer",
                padding: "0",
                font: `${pg === i + 1 ? 700 : 500} 13px ${T.font}`,
              }}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={pg === totalPages}
            style={{
              padding: "7px 14px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: "var(--em-bg-card)",
              cursor: pg === totalPages ? "default" : "pointer",
              font: `500 13px ${T.font}`,
              color: pg === totalPages ? T.fg7 : T.fg1,
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            // Same header-occlusion reason as the add/edit overlay above.
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(360px,100%)",
              background: "var(--em-bg-card)",
              borderRadius: 14,
              padding: 24,
              boxShadow: "0 24px 48px -12px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                font: `700 15px ${T.font}`,
                color: T.fg1,
                marginBottom: 8,
              }}
            >
              Delete "{deleteConfirm.name}"?
            </div>
            <div
              style={{
                font: `400 13px ${T.font}`,
                color: T.fg5,
                marginBottom: 18,
              }}
            >
              This action cannot be undone.
            </div>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: "9px 16px",
                  borderRadius: 9,
                  border: `1px solid ${T.border}`,
                  cursor: "pointer",
                  background: "var(--em-bg-card)",
                  color: T.fg1,
                  font: `600 13px ${T.font}`,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: "9px 18px",
                  borderRadius: 9,
                  border: 0,
                  cursor: "pointer",
                  background: T.danger,
                  color: "#fff",
                  font: `600 13px ${T.font}`,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <ItemModal
          existing={modal.mode === "edit" ? modal.item : null}
          itemLabel={itemLabel}
          placeholder={placeholder}
          extraField={extraField}
          checkboxField={checkboxField}
          onSave={handleSave}
          onClose={() => !busy && setModal(null)}
        />
      )}
    </div>
  );
};

export default SettingsListPanel;
